import React from 'react';

/**
 * The small pieces Audits & policies shares across its screens.
 *
 * Everything derived — overdue, score, review-due — is computed by the API and
 * arrives on the record (see the controllers). Nothing here recalculates it;
 * these helpers only turn what came back into words and colours, so the
 * register, the workspace and the library cannot describe the same record
 * differently.
 */

/** 'Not set' rather than an empty cell: a missing date is information. */
export const formatDate = (value) => {
  if (!value) return 'Not set';
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const formatSize = (bytes) => {
  const n = Number(bytes) || 0;
  return n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(n / 1024)} KB`;
};

/** 'YYYY-MM-DD' for today, and for n days out — used as <input type="date"> bounds. */
export const todayISO = () => new Date().toISOString().slice(0, 10);
export const isoPlusDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export const initials = (name) =>
  (name || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';

/** Stored paths are relative; the API origin turns them into openable URLs. */
const API_ORIGIN = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
export const fileUrl = (path) => (!path ? '' : /^https?:\/\//i.test(path) ? path : `${API_ORIGIN}${path}`);

const TONES = {
  gray: 'bg-[#F1F5F9] text-[#64748B] dark:bg-gray-800 dark:text-gray-400',
  blue: 'bg-[#EEF3FF] text-blue-600 dark:bg-blue-900/20 dark:text-blue-300',
  green: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400',
  amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  red: 'bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400',
};

export const Badge = ({ tone = 'gray', children, className = '' }) => (
  <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-[3px] rounded-md whitespace-nowrap ${TONES[tone] || TONES.gray} ${className}`}>
    {children}
  </span>
);

/**
 * An audit's status, as one badge.
 *
 * Overdue wins over everything except completed — a scheduled audit three
 * weeks past its date is not meaningfully "Scheduled", and showing it that way
 * is how a register stops being trusted.
 */
export const auditStatus = (audit) => {
  if (audit.status === 'completed') return { tone: 'green', label: 'Completed' };
  if (audit.overdue) return { tone: 'red', label: 'Overdue' };
  if (audit.status === 'in_progress') return { tone: 'blue', label: 'In progress' };
  return { tone: 'gray', label: 'Scheduled' };
};

export const actionStatus = (action) => {
  if (action.status === 'done') return { tone: 'green', label: 'Completed' };
  if (action.overdue) return { tone: 'red', label: 'Overdue' };
  return { tone: 'amber', label: 'Open' };
};

/** The library badge. `state` is decided by the API, not re-derived here. */
export const policyStatus = (policy) => ({
  archived: { tone: 'gray', label: 'Archived' },
  draft: { tone: 'gray', label: 'Draft' },
  review_overdue: { tone: 'red', label: 'Review overdue' },
  review_due: { tone: 'amber', label: 'Review due' },
  published: { tone: 'green', label: 'Published' },
}[policy.state] || { tone: 'gray', label: 'Draft' });

/**
 * How a policy revision is stored. `format` is 'text' (written here) or one of
 * the upload kinds — 'pdf', or 'doc' covering both .doc and .docx.
 *
 * Kept as three helpers rather than scattered `=== 'pdf'` checks because the
 * library, the viewer and the publish dialog all describe the same revision
 * and must not word it differently.
 */
export const isUploadedPolicy = (revision) => ['pdf', 'doc'].includes(revision?.format);

export const policyFormatLabel = (revision) => ({
  pdf: 'PDF',
  doc: 'Word document',
}[revision?.format] || 'Written policy');

/**
 * Can a browser show this inline?
 *
 * PDFs render in an iframe. Word documents cannot be displayed by any browser
 * without converting them first, so the viewer offers the download instead of
 * an empty frame — showing a blank box and calling it a preview is worse than
 * plainly saying it has to be opened.
 */
export const canPreviewInline = (revision) => revision?.format === 'pdf';

/** The three checklist answers, in the order they are offered. */
export const ANSWERS = [
  { value: 'met', label: 'Met' },
  { value: 'needs', label: 'Needs action' },
  { value: 'na', label: 'N/A' },
];

/** The shared card shell — one border, radius and shadow for every panel. */
export const Card = ({ className = '', children }) => (
  <section className={`bg-white dark:bg-[#262b33] rounded-[16px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${className}`}>
    {children}
  </section>
);

export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center text-center py-14 px-6">
    {Icon && (
      <span className="w-12 h-12 rounded-2xl bg-[#F1F5F9] dark:bg-gray-800 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-[#94A3B8]" strokeWidth={1.8} />
      </span>
    )}
    <h3 className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100">{title}</h3>
    {description && <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1.5 max-w-sm">{description}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

/** A read-only field in a metadata grid. */
export const MetaItem = ({ label, children }) => (
  <div>
    <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#94A3B8] dark:text-gray-500">{label}</div>
    <div className="text-[13px] font-semibold text-[#0B1220] dark:text-gray-100 mt-1 break-words">{children}</div>
  </div>
);

/** The label above a form control, matching FormField's floating label. */
export const FieldLabel = ({ children, required, hint }) => (
  <div className="flex items-baseline justify-between gap-3 mb-1.5">
    <label className="text-[12px] font-semibold text-[#334155] dark:text-gray-300">
      {children} {required && <span className="text-rose-500">*</span>}
    </label>
    {hint && <span className="text-[11px] text-[#94A3B8] dark:text-gray-500">{hint}</span>}
  </div>
);

export const inputClass =
  'w-full border border-gray-200 dark:border-gray-800 rounded-lg px-3.5 py-2.5 text-[13.5px] text-gray-700 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 bg-transparent focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all';

/** An inline notice. `tone` matches the Badge palette. */
export const Notice = ({ tone = 'gray', children, className = '' }) => {
  const tones = {
    gray: 'bg-[#F8FAFC] border-[#E3E9F3] text-[#475569] dark:bg-gray-800/40 dark:border-gray-800 dark:text-gray-300',
    blue: 'bg-[#F5F8FF] border-[#D7E4FC] text-[#1E4FD8] dark:bg-blue-900/15 dark:border-blue-900/40 dark:text-blue-300',
    amber: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/15 dark:border-amber-900/40 dark:text-amber-300',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/15 dark:border-emerald-900/40 dark:text-emerald-300',
    red: 'bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-900/15 dark:border-rose-900/40 dark:text-rose-300',
  };
  return (
    <div className={`text-[12.5px] leading-relaxed border rounded-xl px-4 py-3 ${tones[tone] || tones.gray} ${className}`}>
      {children}
    </div>
  );
};

/**
 * Pull the human message out of an axios failure.
 *
 * The API always sends one, and it is written for the manager — "Add a reason
 * for 2 Needs action answers", not "400". Falling back to the generic string
 * only when there genuinely isn't one keeps that work visible.
 */
export const errorMessage = (error, fallback = 'Something went wrong. Please try again.') =>
  error?.response?.data?.message || error?.message || fallback;
